import * as React from "react";
import { defaultCountries, parseCountry, FlagImage } from "react-international-phone";
import "react-international-phone/style.css";
import {
  isPhoneValid,
  stripDialCode,
  detectCountryIso2,
  getCountryPhoneDetails,
  sanitizeSubscriberDigits,
} from "src/lib/phone";
import { Button as BaseButton } from "src/shadcn/ui/button";
import { Popover as BasePopover, PopoverContent as BasePopoverContent, PopoverTrigger as BasePopoverTrigger } from "src/shadcn/ui/popover";
import { Command as BaseCommand, CommandEmpty as BaseCommandEmpty, CommandGroup as BaseCommandGroup, CommandInput as BaseCommandInput, CommandItem as BaseCommandItem, CommandList as BaseCommandList } from "src/shadcn/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "src/lib/utils";

const Button = BaseButton as React.ComponentType<any>;
const Popover = BasePopover as React.ComponentType<any>;
const PopoverContent = BasePopoverContent as React.ComponentType<any>;
const PopoverTrigger = BasePopoverTrigger as React.ComponentType<any>;
const Command = BaseCommand as React.ComponentType<any>;
const CommandEmpty = BaseCommandEmpty as React.ComponentType<any>;
const CommandGroup = BaseCommandGroup as React.ComponentType<any>;
const CommandInput = BaseCommandInput as React.ComponentType<any>;
const CommandItem = BaseCommandItem as React.ComponentType<any>;
const CommandList = BaseCommandList as React.ComponentType<any>;

export { isPhoneValid };

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  defaultCountry?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  size?: "default" | "lg";
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = "",
      onChange,
      onBlur,
      defaultCountry = "gh",
      placeholder,
      disabled = false,
      className,
      error = false,
      size = "default",
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [selectedIso2, setSelectedIso2] = React.useState(() => detectCountryIso2(value, defaultCountry));

    const currentCountry = React.useMemo(
      () => defaultCountries.map(parseCountry).find((c) => c.iso2 === selectedIso2) ?? parseCountry(defaultCountries[0]),
      [selectedIso2]
    );

    const countryDetails = React.useMemo(() => getCountryPhoneDetails(selectedIso2), [selectedIso2]);

    const [displayValue, setDisplayValue] = React.useState(() => {
      const iso2 = detectCountryIso2(value, defaultCountry);
      const country = defaultCountries.map(parseCountry).find((c) => c.iso2 === iso2);
      const raw = country ? stripDialCode(value, country.dialCode) : value;
      return sanitizeSubscriberDigits(raw, countryDetails.maxDigits);
    });

    React.useEffect(() => {
      if (!value) {
        setDisplayValue("");
        return;
      }
      const iso2 = detectCountryIso2(value, defaultCountry);
      const country = defaultCountries.map(parseCountry).find((c) => c.iso2 === iso2);
      if (country) {
        setSelectedIso2(iso2);
        const raw = stripDialCode(value, country.dialCode);
        const details = getCountryPhoneDetails(iso2);
        setDisplayValue(sanitizeSubscriberDigits(raw, details.maxDigits));
      } else {
        setDisplayValue(value);
      }
    }, [value, defaultCountry]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = sanitizeSubscriberDigits(e.target.value, countryDetails.maxDigits);
      setDisplayValue(digits);
      onChange?.(digits ? `+${currentCountry.dialCode}${digits}` : "");
    };

    const handleCountrySelect = (iso2: string) => {
      setSelectedIso2(iso2);
      setOpen(false);
      const newCountry = defaultCountries.map(parseCountry).find((c) => c.iso2 === iso2);
      if (newCountry) {
        const details = getCountryPhoneDetails(iso2);
        const digits = sanitizeSubscriberDigits(displayValue, details.maxDigits);
        setDisplayValue(digits);
        onChange?.(digits ? `+${newCountry.dialCode}${digits}` : "");
      }
    };

    return (
      <div
        className={cn(
          "flex items-center rounded-xl border border-input bg-input/50 focus-within:ring-2 focus-within:ring-ring/30 focus-within:border-ring transition-all",
          error && "border-destructive",
          disabled && "opacity-50 pointer-events-none",
          className
        )}
      >
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "flex items-center gap-1.5 px-3 font-normal shrink-0 rounded-l-xl rounded-r-none border-r border-border/60 hover:bg-accent/50",
                size === "lg" ? "h-11 text-base" : "h-9 text-sm"
              )}
            >
              <FlagImage iso2={currentCountry.iso2} className="h-3.5 w-5 rounded-xs object-cover" />
              <span className="text-xs font-mono font-medium">+{currentCountry.dialCode}</span>
              <ChevronsUpDown className="h-3 w-3 opacity-50 ml-0.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country or code..." className="h-9" />
              <CommandList className="max-h-60 overflow-y-auto">
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {defaultCountries.map((c) => {
                    const parsed = parseCountry(c);
                    const isSelected = parsed.iso2 === selectedIso2;
                    return (
                      <CommandItem
                        key={parsed.iso2}
                        value={`${parsed.name} +${parsed.dialCode} ${parsed.iso2}`}
                        onSelect={() => handleCountrySelect(parsed.iso2)}
                        className="flex items-center justify-between text-sm py-2 px-3 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <FlagImage iso2={parsed.iso2} className="h-4 w-6 rounded-sm object-cover shrink-0" />
                          <span className="truncate">{parsed.name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs font-mono text-muted-foreground">+{parsed.dialCode}</span>
                          {isSelected && <Check className="h-4 w-4 text-primary" />}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <input
          ref={inputRef}
          type="tel"
          value={displayValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={placeholder || countryDetails.placeholder}
          disabled={disabled}
          className={cn(
            "flex-1 bg-transparent px-3 py-1 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed text-foreground",
            size === "lg" ? "h-11 text-base" : "h-9 text-sm"
          )}
        />
      </div>
    );
  }
);
PhoneInput.displayName = "PhoneInput";
